import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Grid,
  Pagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  ListItem,
  Button,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  DialogActions,
  Alert,
  Stack,
  Tooltip,
  ListItemText,
} from '@mui/material';
import { IPurchaseOrder } from '../../store/interfaces/IPurchaseOrder';
import { PAYMENT_METHODS_ICONS } from '../../constants/icons';
import { PURCHASE_ORDER_STATUS } from '../../constants/purchaseOrderStatus';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Check, CheckCircle, Close, ContentCopy, DeleteForever, Visibility, VisibilityOff } from '@mui/icons-material';
import { useIsMobileView } from '../../utils/responsive';
import { useAppSelector } from '../../store/hooks/useAppSelector';
import { RootState } from '../../store/types/rootState';
import { useGenerateTicketsMutation, usePaymentCommitTransactionMutation, useRejectPurchaseOrderMutation } from '../../store/api';
import { enqueueSnackbar } from 'notistack';
import { BANK_ACCOUNTS, BankAccountItem } from '../../constants/bankAccounts';
import { IRaffle } from '../../store/interfaces/IRaffle';
import { getVoucherImageUrl, VoucherPreview, VoucherStatusChip } from '../common/VoucherPreview';

interface IPurchaseOrderProps {
  purchaseOrders: IPurchaseOrder[];
  selectedRaffle: IRaffle;
  hasReferenceNumber: boolean;
  refetch: () => void;
}

export const PendingWork = ({ purchaseOrders, selectedRaffle, refetch, hasReferenceNumber }: IPurchaseOrderProps) => {
  const [statusFilter, setStatusFilter] = useState('TRANSFER_PAYMENT_CREATED');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);  
  const [page, setPage] = useState(0);
  const rowsPerPage = 120;
  const [orderId, setOrderId] = useState<string>("")
  const [referenceNumber, setReferenceNumber] = useState<string | undefined>()
  const [openConfirmPurchaseOrderRejectModal, setOpenConfirmPurchaseOrderRejectModal] = React.useState(false);
  const isMobile = useIsMobileView();
  const [openImageModal, setOpenImageModal] = React.useState(false);
  const [openSingleImageModal, setOpenSingleImageModal] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const loggedInAccountId = useAppSelector(
    ({ account }: RootState) => account.id,
  );

  const [copied, setCopied] = useState(false);
  const handleCopyOne = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
          enqueueSnackbar(`Se copiaron los datos!`, {
            variant: "info",
            style: {color: 'black'},
            anchorOrigin: {
                horizontal: "right",
                vertical: "bottom"
            },
        })
        setTimeout(() => setCopied(false), 1500);
    };

  const handleValidateStatus = (order: IPurchaseOrder) => {
    if (
      order.paymentMethod === 'transfer' &&
      order.status === 'TRANSFER_PAYMENT_CREATED'
    ) {
      setOrderId(order.id)
      if (order.referenceNumber) {
        setReferenceNumber(order.referenceNumber)
      }
      setSelectedImage(getVoucherImageUrl(loggedInAccountId, selectedRaffle.id, order, selectedRaffle.type));
      setOpenImageModal(true);
    }
  };

  const handleCloseImageModal = () => {
      setSelectedImage(null);
      setReferenceNumber(undefined)
      setOpenImageModal(false);
  };
  
  const handleCloseSingleImageModal = () => {
      setSelectedImage(null);
      setReferenceNumber(undefined)
      setOpenSingleImageModal(false);
  };

  const [
    paymentCommit, { data, isLoading: isLoadingCommitTransaction },
  ] = usePaymentCommitTransactionMutation();


  const [
    generateTickets,
    {
      data: generateTicketsResponse,
      isLoading: isLoadingGenerateTickets,
    },
  ] = useGenerateTicketsMutation();

  useEffect(() => {
    if (data?.purchaseOrder && data.status !== "FAILED") {
      generateTickets({
        config: {
          purchaseOrderId: data.purchaseOrder,
          accountId: loggedInAccountId,
        },
        raffleId: selectedRaffle.id,
      });
    } else if (data?.status === "FAILED") {
      enqueueSnackbar("Error al momento validar el pago!", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    }
  }, [data, generateTickets, loggedInAccountId, selectedRaffle.id]);

  useEffect(() => {
    if (generateTicketsResponse && generateTicketsResponse.status === "SUCCESSFUL") {
      enqueueSnackbar("Tickets generados exitosamente!", {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      }); 
      refetch();
      setOpenImageModal(false);
    } else if (generateTicketsResponse && generateTicketsResponse.status === "PROCESS_INITIALIZED") {
      refetch();
      enqueueSnackbar(`Se están generando los tickets, enviaremos el correo al usuario con sus números.`, {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      }); 
    }
  }, [setOpenImageModal, generateTicketsResponse])


  

  const handleCommitTransaction = (orderId: string) => {
    setOrderId(orderId)
    paymentCommit({
      conf: {
        paymentMethodId: 'transfer',
        orderId,
        password
      },
      raffleId: selectedRaffle.id,
    });
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };


  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      const jsDate = new Date(date);
      jsDate.setHours(0, 0, 0, 0);
      setStartDate(jsDate);
    } else {
      setStartDate(null);
    }
    setPage(0);
  };
  
  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
    } else {
      setEndDate(null);
    }
    setPage(0);
  };



  const handlePaymentMethodChange = (event: any) => {
    setPaymentMethodFilter(event.target.value);
    setPage(0);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(event.target.value.toLowerCase());
    setPage(0);
  };
  

  const filteredData = purchaseOrders
  .filter((order: IPurchaseOrder) => {
    if (statusFilter && order.status !== statusFilter) {
      return false;
    }
    if (emailFilter && !order.purchaser.email.toLowerCase().includes(emailFilter)) {
      return false;
    }
    if (nameFilter && !order.purchaser.name.toLowerCase().includes(nameFilter)) {
      return false;
    }
    if (startDate && dayjs(order.createdDate) < dayjs(startDate).hour(0).minute(0).second(0)) {
      return false;
    }
    if (endDate && dayjs(order.createdDate) > dayjs(endDate).hour(23).minute(59).second(59)) {
      return false;
    }
    return true;
  })
  .sort((a: IPurchaseOrder, b: IPurchaseOrder) => {
    return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
});

  const pageCount = Math.ceil(filteredData.length / rowsPerPage);

  const currentPageData = filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setReferenceNumber(undefined)
  };

  const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>([]);

  const [
    rejectPurchaseOrder, { data: rejectPurchaseOrderData, isLoading: isLoadingPurchaseOrderRejection },
  ] = useRejectPurchaseOrderMutation();

    useEffect(() => {
      if (selectedRaffle && selectedRaffle.bankAccounts) {
        setBankAccounts(selectedRaffle.bankAccounts)
      } else {
        const config = BANK_ACCOUNTS.find(bankAccount => bankAccount.accountId === loggedInAccountId);
        if (config != undefined) {
          setBankAccounts(config.bankAccounts)
        }
      }
    }, [selectedRaffle])

  const handleClickViewSomething = (order: IPurchaseOrder) => {
    if (
      order.paymentMethod === 'transfer'
    ) {
      setSelectedImage(getVoucherImageUrl(loggedInAccountId, selectedRaffle.id, order, selectedRaffle.type));
      if (order.referenceNumber) {
        setReferenceNumber(order.referenceNumber)
      }
      setOpenSingleImageModal(true);
    }
  }

  const handlePurchaseOrderReject = () => {
    rejectPurchaseOrder({
      raffleId: selectedRaffle.id,
      purchaseOrderId: orderId
    });
  }


  useEffect(() => {
    if (rejectPurchaseOrderData) {
      enqueueSnackbar("Orden de compra rechazada!", {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
      setOpenConfirmPurchaseOrderRejectModal(false)
      setOpenSingleImageModal(false)
      setOpenImageModal(false)
      refetch();
    } 
  }, [rejectPurchaseOrderData]);

    const formateador = new Intl.NumberFormat('es-CL')

  function formatCurrency(order: IPurchaseOrder): string {
    if (!order?.targetAccount?.id) return "";

    const foundedBankAccount = bankAccounts.find(
      bankAccount => bankAccount.bankId === order.targetAccount!.id
    );


    if (!foundedBankAccount) {
      if (order.paymentMethod == 'cash') {
        return "Efectivo"
      }
      return "";
    };

    const totalAmount = order.ticketQuantity * foundedBankAccount.ticketPriceAmount;
    const formattedAmount = formateador.format(totalAmount);
    const currency = foundedBankAccount.ticketCurrency ?? "";

    return `${formattedAmount} ${currency}`;
  }


  return (
    <Box mt={2}>
      <Card variant="outlined">
        <CardContent>
          {purchaseOrders.length === 0 ? (
            <Typography variant="body1">No hay ordenes de compra</Typography>
          ) : (
            <>
              <Box paddingBottom={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={3}>
                    <TextField
                      label="Filtrar por email"
                      variant="outlined"
                      value={emailFilter}
                      onChange={handleEmailChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3}>
                    <TextField
                      label="Filtrar por nombre"
                      variant="outlined"
                      value={nameFilter}
                      onChange={handleNameChange}
                      fullWidth
                    />
                  </Grid>
                  {!isMobile && <>
                  
                    <Grid item xs={12} sm={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                        sx={{
                          width: "100%"
                        }}
                          label="Fecha de Creación"
                          value={startDate}
                          onChange={(date: Date | null) => {
                            handleStartDateChange(date);
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                        sx={{
                          width: "100%"
                        }}
                          label="Hasta"
                          value={endDate}
                          onChange={(date: Date | null) => {
                            handleEndDateChange(date);
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>}
                  <Grid item xs={12} sm={12} md={1}>
                      <Box height={"100%"} width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                        <Button onClick={refetch} variant='contained' size='small'>Recargar</Button>
                      </Box>
                    </Grid>
                </Grid>
              </Box>
              <Dialog
                  open={openConfirmPurchaseOrderRejectModal}
                  onClose={ () => setOpenConfirmPurchaseOrderRejectModal(false)}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <IconButton
                  aria-label="close"
                  onClick={() => setOpenConfirmPurchaseOrderRejectModal(false)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Close />
                </IconButton>
                  <DialogTitle id="alert-dialog-title">
                    {"Confirmar rechazo de la orden de compra"}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Seguro que quiere rechazar la orden de compra? Esta acción no se puede deshacer
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button variant='contained' onClick={handlePurchaseOrderReject}>Confirmar</Button>
                  </DialogActions>
                </Dialog>
              <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer sx={{ maxHeight: "60vh" }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{
                            backgroundColor: "black",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          fecha
                        </TableCell>
                        <TableCell
                          style={{
                            backgroundColor: "black",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          # tickets
                        </TableCell>
                          {hasReferenceNumber && <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            número de referencia
                          </TableCell>}
                            
                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            nombre
                          </TableCell>
                        
                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            #
                          </TableCell>
                        
                        <TableCell
                          style={{
                            backgroundColor: "black",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          email
                        </TableCell>
                        <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            método de pago
                          </TableCell>
                        <TableCell
                          style={{
                            backgroundColor: "black",
                            color: "white",
                            whiteSpace: "nowrap",
                          }}
                        >
                          estado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPageData.map(
                        (order: IPurchaseOrder, index: number) => (
                          <TableRow key={index}>
                            <TableCell sx={{
                              whiteSpace: "nowrap",
                            }}>
                              {dayjs(order.createdDate).format(
                                "YYYY-MM-DD HH:mm:ss"
                              )}
                            </TableCell>
                            <TableCell>
                              {`${order.ticketQuantity} - ${formatCurrency(order)}`}
                            </TableCell>
                            {hasReferenceNumber && <TableCell>{order.referenceNumber} <IconButton onClick={() => handleCopyOne(`${order.referenceNumber}`)} size="small" color="primary">
                                    <ContentCopy fontSize="small" />
                                </IconButton></TableCell>}
                            <TableCell>{order.purchaser.name}</TableCell>
                            <TableCell>{order.purchaser.phone}</TableCell>
                            <TableCell>{order.purchaser.email}</TableCell>
                            <TableCell>
                              <Button onClick={() => handleClickViewSomething(order)}>
                                {PAYMENT_METHODS_ICONS("25px").find(
                                  (icon) => icon.id === order.paymentMethod
                                )?.icon}
                              </Button>
                            </TableCell>
                            <TableCell>
                              {order.status === 'TRANSFER_PAYMENT_CREATED' ? <>
                                <VoucherStatusChip order={order} raffleType={selectedRaffle.type} sx={{ mb: 1, width: '100%' }} />
                                <Stack direction={"row"} spacing={2}>
                                {!isLoadingCommitTransaction && !isLoadingGenerateTickets && <Button
                                  fullWidth
                                  type="submit"
                                  color="error"
                                  sx={{ color: 'white !important' }}
                                  size='small'
                                  variant="contained"
                                  onClick={() => {
                                    setOrderId(order.id)
                                    setOpenConfirmPurchaseOrderRejectModal(true)
                                  }
                                  }
                                >
                                  <Typography color={"#fff"} ml={1} fontWeight={"bold"}>
                                    Cancelar
                                  </Typography>
                                </Button>}

                                {!isLoadingCommitTransaction && !isLoadingGenerateTickets && <Button
                                    fullWidth
                                    type="submit"
                                    color="success"
                                    size='small'
                                    sx={{
                                      mt: 2,
                                    }}
                                    variant="contained"
                                    startIcon={<CheckCircle></CheckCircle>}
                                    onClick={() => handleCommitTransaction(order.id)}
                                  >
                                    <Typography color={"#000"} ml={1} fontWeight={"bold"}>
                                      Generar tickets
                                    </Typography>
                                  </Button>}
                                  {isLoadingCommitTransaction && orderId === order.id && (<LoadingComponent message="Confirmando orden de compra" />)}
                                  {isLoadingGenerateTickets && orderId === order.id && (<LoadingComponent message="Generando tickets" />)}
                                </Stack>

                              </> : <Grid container>
                                <Grid item pl={2} width={'100%'}>
                              <Typography variant="body2">
                                <Chip
                                  label={PURCHASE_ORDER_STATUS.find(pos => pos.id === order.status)?.title}
                                  color={getStatusColor(order.status)}
                                  onClick={() => handleValidateStatus(order)}
                                  variant="outlined"
                                  sx={{
                                    width: '100%',
                                  }}
                                />
                              </Typography>
                                </Grid>
                              </Grid>}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Box mt={2} display={"flex"} justifyContent={"center"}>
                <Pagination
                  count={pageCount}
                  page={page + 1}
                  onChange={(event, value) => setPage(value - 1)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={openSingleImageModal}
        onClose={handleCloseSingleImageModal}
        aria-labelledby="image-modal-title"
        maxWidth="md"
        fullScreen={isMobile}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseSingleImageModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container>
          {referenceNumber && <Grid
              item
              xl={12}
              xs={12}
              sm={12}
              lg={12}
            >
              <Alert sx={{ fontSize: 24 }} severity='warning'>Número de referencia: { referenceNumber }</Alert>
            </Grid>}
          <Grid
              item
              xl={12}
              xs={12}
              sm={12}
              lg={12}
            >
              {selectedImage && (
                <VoucherPreview
                  src={selectedImage}
                  style={{ width: '300px' }}
                />
              )}
            </Grid>
            <Grid
              item
            >
              <Button onClick={handleCloseSingleImageModal}>
                Cerrar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};



interface LoadingComponentProps {
  message: string;
}
const LoadingComponent = ({ message }: LoadingComponentProps) => (
  <Box
    m={3}
    display="flex"
    flexDirection="column"
    alignItems="center"
    width="100%"
    justifyContent="center"
  >
    <Typography>{message}</Typography>
    <CircularProgress
      variant="indeterminate"
      sx={{
        color: "#000",
      }}
    />
  </Box>
);

const getStatusColor = (status: string): "success" | "error" | "info" => {
  switch (status) {
    case 'PROCESS_FINISHED':
      return 'success';
    case 'PAYMENT_FAILED':
    case 'SENDING_MAIL_ERROR':
    case 'PURCHASE_ORDER_REJECTED':
    case 'CASH_PAYMENT_BAD_CREDENTIALS':
      return 'error';
    default:
      return 'info';
  }
};
